import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { usePageSize } from '../../hooks/usePageSize';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MainContentBox, MainDataGridNoRowHover, DataGridListScrollBox, PageTitleToolbarGrid } from '../../util/SharedStyles';
import { GridRenderCellParams, GridValueGetterParams, GridColumns, GridRowModel, GridOverlay, GridSortModel, useGridApiContext, GridSortItem, GridSelectionModel, GridToolbarContainer, GridCsvExportOptions, GridApi, GridRowParams } from '@mui/x-data-grid-pro';
import { Button, CircularProgress, Link, Tooltip, Typography, styled } from '@mui/material';
import { debounce } from 'lodash';

import { useTitle, capitalizeWithUnderscoreRemoval, PremadeErrorLogFilterSet } from '../../util/Common';
import { ToastSeverity, Viewer, eventLogErrorsFilterDate, pageSizeOptions } from '../../util/Constants';
import { getShortDateString, getTimeString } from '../../util/DateTimeUtility';

import PageTitleBar from '../../components/PageTitleBar';
import ErrorMessage from '../../components/ErrorMessage';
import NoResultsMessage from '../../components/NoResultsMessage';
import { clearState, selectError, selectRequestsInFlight, selectErrorLogList, selectErrorLogPagingResult, captureUpdateLogsStatus, clearError, selectUpdateLogsStatus, selectCurrentPage } from './ErrorLogsSlice';
import { fetchErrorLogList, updateTransactionErrorStatus } from './ErrorLogsActions';
import MainDataGridLoadingSkeleton from '../../components/MainDataGridLoadingSkeleton';
import ErrorLogsFilterBar from '../../components/filters/ErrorLogsFilterBar';
import FiltersButton from '../../components/buttons/FiltersButton';
import GridCellDualVert from '../../components/listItems/GridCellDualVert';
import { GetTransactionErrorsInput, TransactionErrorOrderByInput, OrderDirection, ProcessName, RequestResult, ExceptionStatus, TransactionDirection, NoteModel } from '../../gql-types.generated';
import { viewerCanEdit } from '../../util/ViewerUtility';
import { setToastConfig } from '../EDIContainer/EDIContainerSlice';
import ErrorLogResolveDetailsDialog from '../../components/dialogs/ErrorLogResolveDetailsDialog';

const DetailsLink = styled(Link)((props) => ({
    cursor: 'pointer',
}));

const ToolbarButton = styled(Button)`
     margin-right: 4px;
`;

interface ErrorLogsProps {
    viewer: Viewer | undefined;
}

const ErrorLogs: React.FC<ErrorLogsProps> = (props) => {
    const { premadeQuery } = useParams();
    const { viewer } = props;
    const dispatch = useAppDispatch();    
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isResolving, setIsResolving] = useState(false);
    const [contentAreaHeight, setContentAreaHeight] = useState('200px'); //need an actual default height so grid doesn't error on load
    const [openResolveNoteDialog, setOpenResolveNoteDialog] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [filtersCleared, setFiltersCleared] = useState(false);
    const [filterClientName, setFilterClientName] = useState<string | undefined>(undefined);
    const [filterClientId, setFilterClientId] = useState<string | undefined>(undefined);
    const [filterPartnerName, setFilterPartnerName] = useState<string | undefined>(undefined);
    const [filterPartnerId, setFilterPartnerId] = useState<string | undefined>(undefined);
    const [filterPO, setFilterPO] = useState<string | undefined>(undefined);
    const [filterDocNumber, setFilterDocNumber] = useState<string | undefined>(undefined);
    const [filterProcessName, setFilterProcessName] = useState<ProcessName | undefined>(undefined);
    const [filterStatus, setFilterStatus] = useState<ExceptionStatus | undefined>(undefined);
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(undefined);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(undefined);
    const [filterDirection, setFilterDirection] = useState<string | undefined>(undefined);
    const [filterDocumentType, setFilterDocumentType] = useState<string | undefined>(undefined);
    const [filterFileName, setFilterFileName] = useState<string | undefined>(undefined);
    const [filterCategoryId, setFilterCategoryId] = useState<string | undefined>(undefined);
    const [filterCount, setFilterCount] = useState(0);
    const [premadeFilter, setPremadeFilter] = useState<PremadeErrorLogFilterSet | undefined | null>(null);
    const [restoreFromActive, setRestoreFromActive] = useState(false);
    const [errorLogRows, setErrorLogRows] = useState<GridRowModel[] | undefined>(undefined);
    const [debounceOn, setDebounceOn] = useState(false);
    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>();
    const [sortFirstLoad, setSortFirstLoad] = useState(true);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'documentSendReceiveDate',
            sort: 'desc',
        },
    ]);
    const [serverSortModel, setServerSortModel] = useState<TransactionErrorOrderByInput[] | undefined>([{
        createdTime: OrderDirection.Desc
    } as TransactionErrorOrderByInput]);
    const [pageSize, handlePageSizeChange] = usePageSize('errorLogs');
    const [totalCount, setTotalCount] = useState(0);
        
    const errorLogs = useAppSelector(selectErrorLogList);
    const error = useAppSelector(selectError);
    const requestsInFlight = useAppSelector(selectRequestsInFlight);
    const errorLogsPagingResult = useAppSelector(selectErrorLogPagingResult);
    const updateStatus = useAppSelector(selectUpdateLogsStatus);
    const currentPage = useAppSelector(selectCurrentPage);

    const canEdit = viewerCanEdit(viewer);
    
    useTitle("Error Logs");

    const saveActiveFiltersRef = useRef<(() => void) | null>(null);
    
    const debounceOnSortingChanged = useRef(
        debounce((servSortModel) => {
            onSortModelChanged(servSortModel);
        }, 1000)
    ).current;

    // Separate effect for handling back navigation from details
    useEffect(() => {
        const fromDetails = location.state?.fromDetails === true;
        
        if (fromDetails) {
            // Clear old data before restoration
            dispatch(clearState());

            // wrap in timeout to allow for state to clear before restoring
            setTimeout(() => {
                // Set restore flag first before any navigation
                setRestoreFromActive(true);
                setPremadeFilter(undefined);
                // Use window.history to clear state without causing re-render
                // to clear the fromDetails flag for potential refreshes
                window.history.replaceState({}, '', location.pathname);
            }, 200);
        }
    }, [location.state?.fromDetails]);

    // Effect for initialization and premade queries
    useEffect(() => {
        // Check location state FIRST before anything else (synchronous check)
        const fromDetails = location.state?.fromDetails === true;
                
        // Only apply preset if we're NOT coming from details AND not restoring from active
        // Need both checks because restoreFromActive isn't immediately in state
        if (!fromDetails && !restoreFromActive) {
            dispatch(clearState());
            setTimeout(() => {
                setDebounceOn(true);
            }, 200);

            if (premadeQuery) {
                // Coming from dashboard with preset - apply it
                applyPreset();
            } else {
                // Normal navigation - FilterBar will load from localStorage
                setPremadeFilter(undefined);
            }
        }
    }, [premadeQuery, restoreFromActive]);


    useEffect(() => {
        if (totalCount > 0) {
            // Refresh list for new page size
            loadPage(undefined);
        }
    }, [pageSize]);

    useEffect(() => {
        if (errorLogsPagingResult?.totalCount) {
            setTotalCount(errorLogsPagingResult.totalCount);
        }
    }, [errorLogsPagingResult?.totalCount]);
    

    useEffect(() => {
        setErrorLogRows(getErrorLogRows());
        if (!errorLogs && requestsInFlight > 0) {
            setIsLoading(true);
        } else if (requestsInFlight > 0) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [errorLogs, requestsInFlight]);

    useEffect(() => {
        if (updateStatus?.result === RequestResult.Success) {
            dispatch(setToastConfig({
                message: updateStatus.message as string,
                severity: ToastSeverity.Success
            }));
            onResolveNoteDialogClose();
            // Refresh list to bring in potential updates
            loadPage(undefined);
        } else if (updateStatus?.result === RequestResult.Fail) {
            dispatch(setToastConfig({
                message: updateStatus.message as string,
                severity: ToastSeverity.Error
            }));
            onResolveNoteDialogClose();
        }
    }, [updateStatus?.result]);


    useEffect(() => {
        // we have content, so lets properly size that content area        
        const titleHeight = document.getElementById('error-logs-title-comp')?.clientHeight || 0;
        // if filters are open, include that in the calculation
        let filterBarHeight = 0;
        if (filtersOpen) {
            filterBarHeight = document.getElementById('error-logs-filter-bar')?.clientHeight || 0;
        }
        let totalHeaderAreaHeight = titleHeight + filterBarHeight;
        if (totalHeaderAreaHeight > 0) {
            setContentAreaHeight(`calc(100% - ${totalHeaderAreaHeight}px)`);
        }
    }, [errorLogs, filtersOpen]);

    useEffect(() => {
        if (errorLogs) {
            let numFilters = 0;

            if (filterClientName && filterClientName.length > 0) {
                numFilters += 1;
            }
            if (filterDirection && filterDirection.length > 0) {
                numFilters += 1;
            }
            if (filterDocumentType && filterDocumentType.length > 0) {
                numFilters += 1;
            }
            if (filterPartnerName && filterPartnerName.length > 0) {
                numFilters += 1;
            }
            if (filterPO && filterPO.length > 0) {
                numFilters += 1;
            }
            if (filterDocNumber && filterDocNumber.length > 0) {
                numFilters += 1;
            }
            if (filterStatus && filterStatus.length > 0) {
                numFilters += 1;
            }
            if (filterProcessName && filterProcessName.length > 0) {
                numFilters += 1;
            }
            if (filterFileName && filterFileName.length > 0) {
                numFilters += 1;
            }
            if (filterCategoryId && filterCategoryId.length > 0) {
                numFilters += 1;
            }
            if (filterDateFrom) {
                numFilters += 1;
            }
            if (filterDateTo) {
                numFilters += 1;
            }

            setFilterCount(numFilters);
        }
    }, [errorLogs, filterClientName, filterDirection, filterDocumentType, filterPartnerName, filterPO, filterDocNumber, filterStatus, filterProcessName, filterDateFrom, filterDateTo, filterFileName, filterCategoryId]);

    useEffect(() => {
        return () => {
            debounceOnSortingChanged.cancel();
        };
    }, [debounceOnSortingChanged]);

    const getSortRefreshInput = () => {
        return {
            after: undefined,
            limit: pageSize,
            clientName: filterClientName,
            clientId: filterClientId,
            direction: filterDirection,
            documentType: filterDocumentType,
            order: serverSortModel,
            partnerName: filterPartnerName,
            partnerId: filterPartnerId,
            purchaseOrderNumber: filterPO,
            documentId: filterDocNumber,
            status: filterStatus,
            processName: filterProcessName,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            originalFileName: filterFileName,
            categoryId: filterCategoryId
        } as GetTransactionErrorsInput
    }

    useEffect(() => {
        if (!sortFirstLoad) {
            const sortRefreshData = getSortRefreshInput();
            if (debounceOn !== true) {
                onSortModelChanged(sortRefreshData);
            } else {
                debounceOnSortingChanged(sortRefreshData);
            }
        } else {
            setSortFirstLoad(false);
        }
    }, [serverSortModel]);

    const getSelectedRowLog = useCallback((rowId: string) => () => {
        if (rowId && errorLogs?.length) {
            let log = errorLogs.find(l => l.id === rowId);
            return log;
        }
        return undefined;
    }, [errorLogs]);

    const getErrorLogRows = () => {
        if (errorLogs && errorLogs.length > 0) {
            return errorLogs.map((errorLog) => {
                const {
                    id,
                    client,
                    documentReferenceId,
                    purchaseOrderNumber,
                    createdTime,
                    processName,
                    documentId,
                    originalFileName,
                    status,
                    partner,
                    rawMessage,
                    transaction,
                    clientPartner, 
                    category,
                    notes
                } = errorLog;
                
                let formattedNotes = '';
                if (notes && notes.length > 0) {
                    const filteredNotes = notes?.filter((note): note is NoteModel => note !== null && note.note !== null);
                    // Remove HTML tags and join notes with pipe separator for legibility in excel export
                    formattedNotes = filteredNotes
                        .map(note => note.note.replace(/<[^>]*>/g, ''))
                        .join(' | ');
                }
                                    
                return {
                    _raw: errorLog,
                    id,
                    clientName: client?.name,
                    clientId: client?.id,
                    documentNumber: documentId,
                    PONumber: purchaseOrderNumber,
                    documentType: transaction?.name,
                    direction: transaction?.direction,
                    documentSendReceiveDate: createdTime,
                    referenceId: documentReferenceId,
                    partnerName: partner?.name,
                    partnerId: partner?.id,
                    clientPartnerId: clientPartner?.id,
                    fileName: originalFileName,
                    processName,
                    status,
                    rawMessage,
                    category: category?.name,
                    notes: formattedNotes
                } as GridRowModel;
            }) as GridRowModel[];
        } else {
            return [];
        }
    };

    const onRowClientNameClick = (id: string | undefined) => {
        if (id) {
            // Save active filters before navigating
            saveActiveFiltersRef.current?.();
            let route = "/client/" + id;
            navigate(route, { state: { previousPath: location.pathname } });
        }
    };

    const onRowPartnerNameClick = (id: string | undefined) => {
        if (id) {
            // Save active filters before navigating
            saveActiveFiltersRef.current?.();
            let route = "/partner/" + id;
            navigate(route, { state: { previousPath: location.pathname } });
        }
    };

    const onRowDocumentTypeClick = (clientPartnerId: string | undefined) => {
        // navigate to the clientPartner to allow the user to view the associated transaction/documentType
        if (clientPartnerId) {
            // Save active filters before navigating
            saveActiveFiltersRef.current?.();
            let route = "/clientpartner/" + clientPartnerId;
            navigate(route, { state: { previousPath: location.pathname } });
        }
    };

    const onRowStatusClick = (rowId: string) => {
        if (rowId) {
            // Save active filters before navigating to details
            saveActiveFiltersRef.current?.();
            navigate('/errorlog/' + rowId, { state: { previousPath: location.pathname } });
        }
    };
 
    const errorLogColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                headerName: 'CLIENT',
                field: 'clientName',
                flex: 2,
                minWidth: 180,
                cellClassName: "ediDataGridCellFirstChild",
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const clientId = row.clientId;
                    
                    if (clientId) {
                        return (
                            <Link
                                component="button"
                                className="ediDataGridCellFirstChild ediDataGridCellOverflowHidden"
                                onClick={() => {
                                    onRowClientNameClick(clientId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'DOC #',
                field: 'documentNumber', //documentId
                flex: 1,
                minWidth: 100,
                sortable: true,
            }, {
                headerName: 'PO #',
                field: 'PONumber',
                flex: 2,
                minWidth: 180,
                sortable: true,
            }, {
                headerName: 'I/O',
                field: 'direction',
                width: 60,
                sortable: false,
                valueGetter: (params: GridValueGetterParams) => {
                    const { value } = params;
                    let displayValue = value === TransactionDirection.Inbound ? 'I' : value === TransactionDirection.Outbound ? 'O' : '';
                    return displayValue;
                },
            }, {
                headerName: 'TYPE',
                field: 'documentType',
                width: 90,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const clientPartnerId = row.clientPartnerId;
                    
                    if (clientPartnerId) {
                        return (
                            <Link
                                component="button"
                                variant="body2"
                                className="ediDataGridCellOverflowHidden"
                                onClick={() => {
                                    onRowDocumentTypeClick(clientPartnerId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'DOC S/R',
                field: 'documentSendReceiveDate', //createdTime
                width: 114,
                sortable: true,
                type: 'dateTime',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let dateValue = value ? getShortDateString(value) : undefined;
                    let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                    return (
                        <GridCellDualVert header={dateValue} sub={timeValue} />
                    )
                },
            }, {
                headerName: 'REF #',
                field: 'referenceId',
                flex: 1,
                minWidth: 180,
                sortable: true,
            }, {
                headerName: 'PARTNER',
                field: 'partnerName',
                minWidth: 140,
                flex: 2,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const partnerId = row.partnerId;
                    if (partnerId) {
                        return (
                            <Link
                                component="button"
                                variant="body2"
                                className="ediDataGridCellOverflowHidden"
                                onClick={() => {
                                    onRowPartnerNameClick(partnerId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'FILE NAME',
                field: 'fileName',
                flex: 2,
                minWidth: 200,
                sortable: false,
            }, {
                headerName: 'PROCESS NAME',
                field: 'processName',
                flex: 2,
                minWidth: 180,
                sortable: false,
                valueGetter: (params: GridValueGetterParams) => {
                    const { value } = params;
                    return capitalizeWithUnderscoreRemoval(value) ?? '';
                },
            }, {
                headerName: 'CATEGORY',
                field: 'category',
                flex: 1,
                minWidth: 180,
                sortable: false,
            },{
                headerName: 'STATUS',
                field: 'status',
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const displayValue = capitalizeWithUnderscoreRemoval(value, ' ');
                    if (value === ExceptionStatus.Error) {
                        var truncatedMessage = row.rawMessage && row.rawMessage.length > 300 ? row.rawMessage.substring(0, 300) + "..." : row.rawMessage;
                        return (
                            <Tooltip
                                title={<Typography style={{fontSize: '14px'}}>{truncatedMessage}</Typography>}
                                placement='left-start'
                            >
                                <DetailsLink onClick={() => {onRowStatusClick(row.id)}} color="error" >{displayValue}</DetailsLink>
                            </Tooltip>
                        )
                    } else if (value === ExceptionStatus.ResolvedError) {
                        return (
                            <DetailsLink onClick={() => {onRowStatusClick(row.id)}} color="primary" >{displayValue}</DetailsLink>
                        )
                    }
                    else {
                        return displayValue;
                    }
                },
            }, {
                headerName: 'NOTES',
                field: 'notes',
                hide: true,
            },{
                headerName: 'MESSAGE',
                field: 'rawMessage',
                hide: true,
            },
        ],
        [],
    );

    const GetApiRef = () => {
        return useGridApiContext();
    };
    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Error Logs' error={error}></ErrorMessage>
                )}
                {!error && (errorLogs && errorLogs.length > 0 && requestsInFlight > 0) && (
                    <CircularProgress aria-label={'progress spinner'} key={'logs-spinner'} size={42} sx={{ zIndex: 1 }} />
                )}
                {!error && (!errorLogs) && (
                    <MainDataGridLoadingSkeleton apiRef={GetApiRef()} rowBottomMargin={6} />
                )}
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Error Logs' error={error}></ErrorMessage>
                )}
                {!error && (errorLogs && errorLogs.length <= 0 && filterCount > 0) && (
                    <NoResultsMessage topMargin={6} message="Try different filters to see results." />
                )}
            </GridOverlay>
        );
    };

    const getServerSortEntryFromGridSortEntry = (entry: GridSortItem) => {
        let newModel = {} as TransactionErrorOrderByInput;
        switch (entry.field) {
            case 'documentSendReceiveDate':
                newModel.createdTime = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'documentNumber':
                newModel.documentId = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'referenceId':
                newModel.documentReferenceId = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            // case 'documentType':
            //     newModel.documentType = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
            //     break;
            case 'PONumber':
                newModel.purchaseOrderNumber = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
        }
        return newModel;
    };

    const createdAndSetServerSortModel = (model: GridSortModel) => {
        if (model && model.length > 0) {
            let newArray = [] as TransactionErrorOrderByInput[];
            let i = 0;
            for (i; i < model.length; ++i) {
                newArray.push(getServerSortEntryFromGridSortEntry(model[i]));
            }
            setServerSortModel(newArray);
        } else {
            setServerSortModel(undefined);
        }

    };

    const onSortModelChange = (model: GridSortModel) => {
        createdAndSetServerSortModel(model);
        setSortModel(model);
    };

    const onFiltersClick = () => {
        // Show/hide filters bar
        setFiltersOpen(!filtersOpen);
        // reset filters cleared to false since not clicking close button on bar at this point
        setFiltersCleared(false);
    };

    const onFilterBarClose = () => {
        // set filters as cleared
        setFiltersCleared(true);
        setFilterCount(0);
        // we don't want to close the filter bar since client is required
        setFiltersOpen(true);
    };

    const onSortModelChanged = (refreshInput: GetTransactionErrorsInput) => {
        dispatch(clearState());
        dispatch(fetchErrorLogList(1, refreshInput));
    };

    const onSelectionModelChange = (currentSelectionModel: GridSelectionModel) => {
        setSelectionModel(currentSelectionModel);
    };

    const handlePageChange = (newPage: number) => {
        const filterInput = {
            limit: pageSize,
            clientName: filterClientName,
            clientId: filterClientId,
            direction: filterDirection,
            documentType: filterDocumentType,
            order: serverSortModel,
            partnerName: filterPartnerName,
            partnerId: filterPartnerId,
            purchaseOrderNumber: filterPO,
            documentId: filterDocNumber,
            status: filterStatus,
            processName: filterProcessName,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            originalFileName: filterFileName,
            categoryId: filterCategoryId
        } as GetTransactionErrorsInput;
        
        dispatch(fetchErrorLogList(newPage, filterInput));
    };

    const loadPage = (targetPage?: number) => {
        const page = targetPage || 1;
        handlePageChange(page);
    };

    
    const refreshFilters = (
        filterClient: string | undefined,
        filterIdOfClient?: string | undefined,
        filterIO?: string | undefined,
        filterDocType?: string | undefined,
        filterPartner?: string | undefined,
        filterIdOfPartner?: string | undefined,
        filterPONum?: string | undefined,
        filterDocNum?: string | undefined,
        filterStatusVal?: ExceptionStatus | undefined,
        filterProcess?: ProcessName | undefined,
        filterDocDateFrom?: DateTime | undefined,
        filterDocDateTo?: DateTime | undefined,
        filterOriginalFileName?: string | undefined,
        filterCategory?: string | undefined
    ) => {
        setFilterClientName(filterClient);
        setFilterClientId(filterIdOfClient);
        setFilterDirection(filterIO);
        setFilterDocumentType(filterDocType);
        setFilterDocNumber(filterDocNum);
        setFilterPartnerName(filterPartner);
        setFilterPartnerId(filterIdOfPartner);
        setFilterPO(filterPONum);
        setFilterStatus(filterStatusVal);
        setFilterProcessName(filterProcess);
        setFilterDateFrom(filterDocDateFrom);
        setFilterDateTo(filterDocDateTo);
        setFilterFileName(filterOriginalFileName);
        setFilterCategoryId(filterCategory);

        dispatch(clearState());
        
        dispatch(fetchErrorLogList(1, {
            limit: pageSize,
            clientName: filterClient,
            clientId: filterIdOfClient,
            direction: filterIO,
            documentType: filterDocType,
            order: serverSortModel,
            partnerName: filterPartner,
            partnerId: filterIdOfPartner,
            purchaseOrderNumber: filterPONum,
            documentId: filterDocNum,
            status: filterStatusVal,
            processName: filterProcess,
            dateFrom: filterDocDateFrom,
            dateTo: filterDocDateTo,
            originalFileName: filterOriginalFileName,
            categoryId: filterCategory
        }));
       
    };

    const onDialogClose = () => {
        // Clear error on close.
        dispatch(clearError());
    };
    
    const getSelectionRows = () => {
        if (selectionModel && selectionModel.length > 0) {
            return selectionModel.map(rowId => rowId.toString());
        }
    };

    const resolveSelectedErrorStatusRows = () => {
        // if the filterProcessName is set to FileMonitor, then call updateTransactionErrorStatus with the FileMonitor process name
        // without a note, otherwise open the dialog to make the user enter a note
        if (filterProcessName === ProcessName.FileMonitor) {
            onResolveNoteDialogSave();
        } else {
            setOpenResolveNoteDialog(true);
        }
    };

    const onResolveNoteDialogCancel = () => {
        setOpenResolveNoteDialog(false);
        onDialogClose();
        dispatch(captureUpdateLogsStatus());
    };

    const onResolveNoteDialogClose = () => {
        setIsResolving(false);
        setOpenResolveNoteDialog(false);
        onDialogClose();
        dispatch(captureUpdateLogsStatus());
        setSelectionModel([]);
    };

    const onResolveNoteDialogSave = (note?: string, errorCategoryId?: string) => {
        dispatch(captureUpdateLogsStatus());

        let ids = getSelectionRows();
        
        // resolve the selected error status rows
        if (ids && ids.length > 0) {
            setIsResolving(true);
            dispatch(updateTransactionErrorStatus({transactionErrorIds: ids, status: ExceptionStatus.ResolvedError, note: note, errorCategoryId: errorCategoryId}));
            
        } else {
            onResolveNoteDialogCancel();
        }
    };

    const applyPreset = () => {
        if (premadeQuery) {
            if (premadeQuery === "errors") {
                applyPresetErrorsFilter();
            } else if (premadeQuery === "map-errors") {
                applyPresetErrorsFilter(true);
            }
        }
    };
    
    const applyPresetErrorsFilter = (isMapping?: boolean) => {
        setPremadeFilter({
            filterStatusVal: ExceptionStatus.Error,
            filterDocDateFrom: eventLogErrorsFilterDate,
            filterProcess: isMapping === true ? ProcessName.MapDataValidation : undefined
        } as PremadeErrorLogFilterSet);
    };

    const isResolveButtonEnabled = () => {
        if (selectionModel && selectionModel.length > 0) {
            let nonErrorRows = selectionModel.filter(rowId => {
                let row = getSelectedRowLog(rowId.toString());
                return row && row()?.status !== ExceptionStatus.Error;
            });
            return nonErrorRows.length === 0;
        }

        return false;
    };

    const handleExport = (options: GridCsvExportOptions, apiRef: React.MutableRefObject<GridApi>) => {
        // use the apiRef to directly call the export function
        // doing this instead of using the export button in the grid toolbar
        // to prerror having to choose the download option from the button dropdown
        apiRef?.current.exportDataAsCsv(options);
    };
        
    const gridToolbar = () => {
        let apiRef=GetApiRef();
        return (
            <GridToolbarContainer>
                {canEdit &&
                <ToolbarButton
                    title="Resolve Selected Errors"
                    aria-label="Resolve Errors"
                    size="small"
                    onClick={resolveSelectedErrorStatusRows}
                    color='primary'
                    disabled={!isResolveButtonEnabled()}
                >
                    Resolve Error
                </ToolbarButton>
                }
                <ToolbarButton
                    title="Export to CSV"
                    aria-label="Export"
                    size="small"
                    onClick={() => handleExport({ allColumns: true }, apiRef)}
                    color='primary'
                    disabled={isLoading || !errorLogRows || errorLogRows.length === 0}
                >
                    Export
                </ToolbarButton>
            </GridToolbarContainer>
        );
    };


    return (
        <MainContentBox>
            <PageTitleBar text='Error Logs' id="error-logs-title-comp">
                <PageTitleToolbarGrid item>
                    <FiltersButton
                        onClick={onFiltersClick}
                        filterCount={filterCount}
                        filtersCleared={filtersCleared}
                        disabled={isLoading}
                        aria-label="filter button"
                        data-cy="filters"
                    />
                </PageTitleToolbarGrid>
            </PageTitleBar>
            <ErrorLogsFilterBar
                id="error-logs-filter-bar"
                visible={filtersOpen}
                loading={isLoading}
                viewer={viewer}
                premadeSet={premadeFilter}
                restoreFromActive={restoreFromActive}
                onFilterChanged={refreshFilters}
                onClose={onFilterBarClose}
                onSaveActiveFilters={(saveFunc) => {
                    saveActiveFiltersRef.current = saveFunc;
                }}
            />
            <DataGridListScrollBox scrollheight={contentAreaHeight}>
                <MainDataGridNoRowHover
                    loading={isLoading}
                    rowHeight={52}
                    aria-label="Error Logs List"
                    disableColumnMenu
                    disableColumnFilter
                    checkboxSelection={canEdit}
                    rows={errorLogRows ?? []}
                    columns={errorLogColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    sortingMode="server"
                    onSortModelChange={onSortModelChange}
                    selectionModel={selectionModel}
                    onSelectionModelChange={onSelectionModelChange}
                    pagination={true}
                    paginationMode="server"
                    page={currentPage - 1}
                    onPageChange={(newPage: number) => handlePageChange(newPage + 1)}
                    pageSize={pageSize}
                    rowsPerPageOptions={pageSizeOptions}
                    onPageSizeChange={handlePageSizeChange}
                    rowCount={totalCount}
                    hideFooterSelectedRowCount
                    disableSelectionOnClick
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        Toolbar: gridToolbar
                    }}
                    componentsProps={{
                        pagination: {
                            disabled: requestsInFlight > 0
                        }
                    }}
                />
            </DataGridListScrollBox>
            <ErrorLogResolveDetailsDialog
                isOpen={openResolveNoteDialog} 
                logStatus={ExceptionStatus.Error} 
                onCancel={onResolveNoteDialogCancel} 
                onSave={onResolveNoteDialogSave} 
            />
            {(isResolving && !openResolveNoteDialog) &&
                <CircularProgress
                    aria-label={'progress spinner'}
                    size={42}
                    sx={{
                        position: 'absolute',
                        top: '70%',
                        left: '50%',
                        marginTop: '-24px',
                        marginLeft: '-24px',
                        zIndex: 1500
                    }}
                />
            }
        </MainContentBox>
    );
}

export default ErrorLogs;
